// Schritt 2 des Übergabe-Assistenten — Rundgang: Räume aus dem Katalog wählen, je
// Raum die Prüfpunkte als i.O./Mangel-Schalter erfassen (bei Mangel Notiz + Fotos)
// und die im Raum stehenden Zähler direkt mit ablesen. Reine Orchestrierung: die
// Innenteile (Prüfpunkt, Zähler-Zeile, Raum-Karte, Foto-Vorschau) liegen in eigenen
// Dateien (Fix-Runde 1 — vorher alles in einer ~450-Zeilen-Datei).
//
// Ladefehler (Katalog, Zähler-Stammdaten) werden angezeigt statt still eine leere
// Liste zu zeigen — auf dem Tablet im Keller sieht ein Funkloch sonst identisch aus
// wie "keine Räume/Zähler vorhanden".
import React, { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase/client';
import type { LokalerEntwurf } from '../../../lib/uebergabe/lokal';
import {
  ladeKatalog, baueRaumBloecke, raumHatErfassteDaten, raumKennung, effektiverRaumName, nummeriereGleicheNamen,
  type KatalogEintrag,
} from '../../../lib/uebergabe/katalog';
import { zaehlerFuerRaum, zuZaehlerEintrag, offeneZaehler, findeEintragImBlock, type ZaehlerStammdaten } from '../../../lib/uebergabe/zaehlerZuordnung';
import { passtZurEinheit } from '../../../lib/uebergabe/einheitVergleich';
import { skaliereBild } from '../../../lib/uebergabe/bild';
import type { Pruefpunkt, ZaehlerEintrag } from '../../../lib/uebergabe/typen';
import RaumKarte from './RaumKarte';
import { Check, Plus, Trash } from '@phosphor-icons/react';
import { useAppMeldungen } from '../../../components/ui/AppMeldungen';
import { useMobilModus } from '../../../lib/immobilien/mobil';
import { raumStil } from '../../../lib/uebergabeAnsicht/darstellung';
import { RAUM_ICON, istTouch, schriftAuf } from './assistent/Rundgangteile';
import LadeHinweis from './LadeHinweis';

// „Sonstiger Raum" ist der einzige Katalog-Eintrag, der mehrfach nebeneinander angelegt
// werden darf (frei benennbar in der Raumkarte, s. RaumKarte.tsx) — ein zweiter Tipp auf
// den Chip fügt deshalb IMMER einen neuen Block hinzu, statt wie bei jedem anderen Raum
// zu toggeln (an/aus). Sonst könnte man nach dem ersten Anlegen keinen zweiten mehr
// hinzufügen, ohne erst den ersten "abzuwählen".
const SONSTIGER_RAUM = 'Sonstiger Raum';

interface SchrittRundgangProps {
  entwurf: LokalerEntwurf;
  setEntwurf: (updater: LokalerEntwurf | ((vorher: LokalerEntwurf) => LokalerEntwurf)) => void;
}


const SchrittRundgang: React.FC<SchrittRundgangProps> = ({ entwurf, setEntwurf }) => {
  const { protokoll } = entwurf;
  const { mobil } = useMobilModus();
  const { bestaetige, dialoge } = useAppMeldungen(mobil);
  const [katalog, setKatalog] = useState<KatalogEintrag[]>([]);
  const [katalogFehler, setKatalogFehler] = useState(false);
  const [zaehlerStamm, setZaehlerStamm] = useState<ZaehlerStammdaten[]>([]);
  const [zaehlerFehler, setZaehlerFehler] = useState(false);

  const ladeKatalogListe = useCallback(async () => {
    setKatalogFehler(false);
    try {
      setKatalog(await ladeKatalog());
    } catch {
      setKatalogFehler(true);
    }
  }, []);
  useEffect(() => { void ladeKatalogListe(); }, [ladeKatalogListe]);

  const ladeZaehlerStamm = useCallback(async () => {
    setZaehlerFehler(false);
    if (!protokoll.objekt_id || !protokoll.einheit_id) { setZaehlerStamm([]); return; }
    const { data: einheit, error: einheitFehler } = await supabase
      .from('objekt_einheiten').select('bezeichnung').eq('id', protokoll.einheit_id).maybeSingle();
    if (einheitFehler) { setZaehlerFehler(true); return; }
    if (!einheit?.bezeichnung) { setZaehlerStamm([]); return; }
    // Alle aktiven Zähler des Objekts laden und clientseitig über passtZurEinheit
    // filtern statt serverseitig auf Gleichheit zu vergleichen — die Bezeichnung
    // ist Freitext und in den Stammdaten uneinheitlich geschrieben ("W1"/"w1").
    const { data, error } = await supabase
      .from('zaehler')
      .select('id, zaehler_nummer, typ, standort, einheit_bezeichnung')
      .eq('objekt_id', protokoll.objekt_id).eq('aktiv', true);
    if (error) { setZaehlerFehler(true); return; }
    const alle = (data ?? []) as ZaehlerStammdaten[];
    setZaehlerStamm(alle.filter(z => passtZurEinheit(z.einheit_bezeichnung ?? null, einheit.bezeichnung)));
  }, [protokoll.objekt_id, protokoll.einheit_id]);
  useEffect(() => { void ladeZaehlerStamm(); }, [ladeZaehlerStamm]);

  // Ein zweiter Tipp auf einen Raum-Chip (oder „Raum entfernen" in der Karte) löscht den
  // Raum samt Prüfpunkten, Notizen, Fotoverweisen und den dort erfassten Zählern. Auf dem
  // Tablet mit 13 Chips nebeneinander ist der Fehlgriff wahrscheinlich, deshalb seit dem
  // Abschluss-Review (Fund W4) eine Rückfrage — aber nur, wenn wirklich etwas verloren
  // ginge: ein versehentlich gewählter, noch unberührter Raum bleibt ohne Nachfrage
  // abwählbar.
  //
  // Entfernt GENAU den Block mit dieser Kennung — wichtig seit „Sonstiger Raum" mehrfach
  // nebeneinander vorkommen kann (Praxis-Ergänzung): vorher war `raum` selbst der
  // Schlüssel, ein Entfernen über den Namen hätte dann alle gleichnamigen Blöcke
  // gleichzeitig getroffen (s. raumKennung() in katalog.ts).
  const entferneRaumBlock = async (kennung: string) => {
    const block = protokoll.raeume.find(r => raumKennung(r) === kennung);
    if (!block) return;
    if (raumHatErfassteDaten(block, protokoll.zaehler)) {
      const bestaetigt = await bestaetige({
        nachricht: `„${effektiverRaumName(block)}“ entfernen?\n\nAlle in diesem Raum erfassten Prüfpunkte, Notizen, Fotos und Zählerstände gehen dabei verloren.`,
        label: 'Entfernen', icon: Trash,
      });
      if (!bestaetigt) return;
    }
    setEntwurf(e => {
      const aktuellerBlock = e.protokoll.raeume.find(r => raumKennung(r) === kennung);
      if (!aktuellerBlock) return e;
      // `?? []`: ein Raumblock aus einem älteren, lokal begonnenen Entwurf könnte
      // `zaehler_ids` selbst vermissen lassen (s. lib/uebergabe/lokal.ts).
      const entferntIds = new Set(aktuellerBlock.zaehler_ids ?? []);
      return {
        ...e,
        protokoll: {
          ...e.protokoll,
          raeume: e.protokoll.raeume.filter(r => raumKennung(r) !== kennung),
          zaehler: e.protokoll.zaehler.filter(z => !entferntIds.has(z.id)),
        },
      };
    });
  };

  /** Legt einen neuen Raumblock an — für einen noch nicht gewählten Raum-Chip ODER
   * (Praxis-Ergänzung) für JEDEN Klick auf „Sonstiger Raum", der immer einen weiteren,
   * eigenständigen Block ergänzt statt zu toggeln (s. SONSTIGER_RAUM oben). */
  const fuegeRaumHinzu = (raum: string) => {
    setEntwurf(e => {
      const neuerBlock = baueRaumBloecke([raum], katalog)[0];
      if (!neuerBlock) return e;
      return { ...e, protokoll: { ...e.protokoll, raeume: [...e.protokoll.raeume, neuerBlock] } };
    });
  };

  // Chip-Klick für einen Raum, der (anders als „Sonstiger Raum") nur einmal vorkommen
  // darf: existiert bereits ein Block, wird er entfernt (mit Rückfrage bei erfasster
  // Arbeit), sonst neu angelegt.
  const toggleRaum = (raum: string) => {
    const vorhandenerBlock = protokoll.raeume.find(r => r.raum === raum);
    if (vorhandenerBlock) { void entferneRaumBlock(raumKennung(vorhandenerBlock)); return; }
    fuegeRaumHinzu(raum);
  };

  const aenderePruefpunkt = (kennung: string, name: string, patch: Partial<Pruefpunkt>) => {
    setEntwurf(e => ({
      ...e,
      protokoll: {
        ...e.protokoll,
        // `?? []`: s. Kommentar an raumFotosHinzufuegen weiter unten — ein Raumblock aus
        // einem älteren Entwurf könnte `pruefpunkte` selbst vermissen lassen.
        raeume: e.protokoll.raeume.map(r => (raumKennung(r) !== kennung ? r : {
          ...r, pruefpunkte: (r.pruefpunkte ?? []).map(p => (p.name !== name ? p : { ...p, ...patch })),
        })),
      },
    }));
  };

  /** Freitext-Bezeichnung bei „Sonstiger Raum" (Praxis-Ergänzung, RaumKarte.tsx) — leer
   * lassen behält „Sonstiger Raum" als Anzeigename (effektiverRaumName in katalog.ts). */
  const aendereRaumBezeichnung = (kennung: string, wert: string) => {
    setEntwurf(e => ({
      ...e,
      protokoll: {
        ...e.protokoll,
        raeume: e.protokoll.raeume.map(r => (raumKennung(r) !== kennung ? r : { ...r, bezeichnung: wert })),
      },
    }));
  };

  const fotosHinzufuegen = async (kennung: string, name: string, dateien: FileList) => {
    const neueBlobs: Record<string, Blob> = {};
    const neuePfade: string[] = [];
    for (const datei of Array.from(dateien)) {
      const skaliert = await skaliereBild(datei);
      const pfad = `${entwurf.id}/fotos/${crypto.randomUUID()}.jpg`;
      neueBlobs[pfad] = skaliert;
      neuePfade.push(pfad);
    }
    setEntwurf(e => ({
      ...e,
      fotos: { ...e.fotos, ...neueBlobs },
      protokoll: {
        ...e.protokoll,
        // `?? []` an BEIDEN Stellen: sowohl der Raumblock (`pruefpunkte`) als auch der
        // einzelne Prüfpunkt (`fotos`) könnten aus einem älteren, lokal begonnenen Entwurf
        // stammen und das jeweilige Feld schlicht nicht tragen (s. raumFotosHinzufuegen
        // unten für den Absturz, der genau daraus entstand).
        raeume: e.protokoll.raeume.map(r => (raumKennung(r) !== kennung ? r : {
          ...r,
          pruefpunkte: (r.pruefpunkte ?? []).map(p => (p.name !== name ? p : { ...p, fotos: [...(p.fotos ?? []), ...neuePfade] })),
        })),
      },
    }));
  };

  /** Raum-Fotos zur Zustandsdokumentation (Praxis-Ergänzung) — dieselbe Skalier-/
   * Ablage-Mechanik wie Prüfpunkt-Fotos (fotosHinzufuegen), aber am Raumblock selbst statt
   * an einem einzelnen Prüfpunkt, unabhängig von dessen Mangel-Status. */
  const raumFotosHinzufuegen = async (kennung: string, dateien: FileList) => {
    const neueBlobs: Record<string, Blob> = {};
    const neuePfade: string[] = [];
    for (const datei of Array.from(dateien)) {
      const skaliert = await skaliereBild(datei);
      const pfad = `${entwurf.id}/fotos/${crypto.randomUUID()}.jpg`;
      neueBlobs[pfad] = skaliert;
      neuePfade.push(pfad);
    }
    setEntwurf(e => ({
      ...e,
      fotos: { ...e.fotos, ...neueBlobs },
      protokoll: {
        ...e.protokoll,
        // KRITISCHER Live-Fund: `fotos` ist ein neues Feld an RaumBlock (s. typen.ts) — ein
        // Entwurf, der lokal auf einem Tablet VOR dieser Änderung angelegt wurde, trägt es
        // im gespeicherten JSON schlicht nicht. `r.fotos` wäre dann `undefined`, und
        // `[...r.fotos, ...]` wirft "r.fotos is not iterable" — der Fotoknopf sitzt an
        // JEDEM Raum, der Absturz war also sofort erreichbar. `?? []` macht diesen
        // Schreibpfad defensiv; vervollstaendigeEntwurf() (lib/uebergabe/lokal.ts) ergänzt
        // das Feld zusätzlich schon beim normalen Laden, damit dieselbe Fehlerklasse beim
        // nächsten neuen Listenfeld nicht wiederkommt.
        raeume: e.protokoll.raeume.map(r => (raumKennung(r) !== kennung ? r : { ...r, fotos: [...(r.fotos ?? []), ...neuePfade] })),
      },
    }));
  };

  // Legt bei Bedarf einen Zähler-Eintrag für einen Stammdatensatz an (falls noch
  // keiner existiert) und liefert dessen lokale Id — gemeinsam genutzt von
  // Stand- und Foto-Erfassung, damit ein Foto vor dem ersten Stand keinen zweiten,
  // doppelten Eintrag erzeugt. `kennung` identifiziert den Block eindeutig (Zuordnung
  // des neuen Zähler-Eintrags), `raumName` ist der zum Anzeigezeitpunkt aktuelle Name
  // (effektiverRaumName) für den reinen Anzeigetext auf dem Zähler-Eintrag selbst.
  //
  // Review-Fund: „bestehend" muss BLOCKGENAU geprüft werden (findeEintragImBlock), nicht
  // nur über `zaehler_id` — sonst teilen sich zwei gleichnamige Raumblöcke (z. B. zwei
  // „Sonstiger Raum" ohne Bezeichnung, denen zaehlerFuerRaum() denselben Stammsatz
  // anbietet) denselben Eintrag: der zweite Block hätte den vom ersten angelegten Eintrag
  // "gefunden" und ihn bei jeder Eingabe mitgeändert, statt eine eigene Ablesung anzulegen.
  const holeOderErzeugeEintrag = (
    e: LokalerEntwurf, kennung: string, raumName: string, stamm: ZaehlerStammdaten,
  ): { entwurf: LokalerEntwurf; eintragId: string } => {
    const block = e.protokoll.raeume.find(r => raumKennung(r) === kennung);
    const bestehend = findeEintragImBlock(e.protokoll.zaehler, block?.zaehler_ids, stamm.id);
    if (bestehend) return { entwurf: e, eintragId: bestehend.id };
    const neu = zuZaehlerEintrag(stamm, raumName);
    return {
      entwurf: {
        ...e,
        protokoll: {
          ...e.protokoll,
          zaehler: [...e.protokoll.zaehler, neu],
          // `?? []`: der Raumblock könnte aus einem älteren Entwurf stammen und
          // `zaehler_ids` selbst vermissen lassen (s. lib/uebergabe/lokal.ts).
          raeume: e.protokoll.raeume.map(r => (raumKennung(r) === kennung ? { ...r, zaehler_ids: [...(r.zaehler_ids ?? []), neu.id] } : r)),
        },
      },
      eintragId: neu.id,
    };
  };

  const standAendernStamm = (kennung: string, raumName: string, stamm: ZaehlerStammdaten, stand: string) => {
    setEntwurf(e => {
      const { entwurf: e2, eintragId } = holeOderErzeugeEintrag(e, kennung, raumName, stamm);
      return { ...e2, protokoll: { ...e2.protokoll, zaehler: e2.protokoll.zaehler.map(z => (z.id === eintragId ? { ...z, stand } : z)) } };
    });
  };

  const fotoHinzufuegenStamm = async (kennung: string, raumName: string, stamm: ZaehlerStammdaten, datei: File) => {
    const skaliert = await skaliereBild(datei);
    const pfad = `${entwurf.id}/fotos/${crypto.randomUUID()}.jpg`;
    setEntwurf(e => {
      const { entwurf: e2, eintragId } = holeOderErzeugeEintrag(e, kennung, raumName, stamm);
      return {
        ...e2,
        fotos: { ...e2.fotos, [pfad]: skaliert },
        protokoll: { ...e2.protokoll, zaehler: e2.protokoll.zaehler.map(z => (z.id === eintragId ? { ...z, foto: pfad } : z)) },
      };
    });
  };

  const standAendernAdHoc = (eintragId: string, stand: string) => {
    setEntwurf(e => ({ ...e, protokoll: { ...e.protokoll, zaehler: e.protokoll.zaehler.map(z => (z.id === eintragId ? { ...z, stand } : z)) } }));
  };

  const fotoHinzufuegenAdHoc = async (eintragId: string, datei: File) => {
    const skaliert = await skaliereBild(datei);
    const pfad = `${entwurf.id}/fotos/${crypto.randomUUID()}.jpg`;
    setEntwurf(e => ({
      ...e,
      fotos: { ...e.fotos, [pfad]: skaliert },
      protokoll: { ...e.protokoll, zaehler: e.protokoll.zaehler.map(z => (z.id === eintragId ? { ...z, foto: pfad } : z)) },
    }));
  };

  // Dieselbe blockgenaue Prüfung wie holeOderErzeugeEintrag oben — sonst würde ein
  // manuelles Hinzufügen aus „Zähler hier erfassen" (RaumKarte.tsx) in einem zweiten,
  // gleichnamigen Raumblock stillschweigend NICHTS tun (der Stammsatz gilt dann als schon
  // erfasst), obwohl in DIESEM Block noch kein eigener Eintrag existiert.
  const zaehlerZuRaumHinzufuegen = (kennung: string, raumName: string, stamm: ZaehlerStammdaten) => {
    setEntwurf(e => {
      const block = e.protokoll.raeume.find(r => raumKennung(r) === kennung);
      if (findeEintragImBlock(e.protokoll.zaehler, block?.zaehler_ids, stamm.id)) return e;
      const neu = zuZaehlerEintrag(stamm, raumName);
      return {
        ...e,
        protokoll: {
          ...e.protokoll,
          zaehler: [...e.protokoll.zaehler, neu],
          raeume: e.protokoll.raeume.map(r => (raumKennung(r) === kennung ? { ...r, zaehler_ids: [...(r.zaehler_ids ?? []), neu.id] } : r)),
        },
      };
    });
  };

  const neuerZaehlerHinzufuegen = (kennung: string, raumName: string, art: string, nummer: string) => {
    if (!art.trim() && !nummer.trim()) return;
    const neu: ZaehlerEintrag = { id: crypto.randomUUID(), zaehler_id: null, art: art.trim(), nummer: nummer.trim(), stand: '', foto: null, raum: raumName, ablesung_id: null };
    setEntwurf(e => ({
      ...e,
      protokoll: {
        ...e.protokoll,
        zaehler: [...e.protokoll.zaehler, neu],
        raeume: e.protokoll.raeume.map(r => (raumKennung(r) === kennung ? { ...r, zaehler_ids: [...(r.zaehler_ids ?? []), neu.id] } : r)),
      },
    }));
  };

  // Tragen zwei Raumblöcke denselben Namen (z. B. zwei „Sonstiger Raum" ohne eigene
  // Bezeichnung), sind sie in der Kartenüberschrift sonst nicht unterscheidbar —
  // nummeriereGleicheNamen() hängt ab dem zweiten Auftreten eine laufende Nummer an. Rein
  // anzeigebezogen (Index-Zuordnung zu protokoll.raeume): `raumName` im Render-Loop unten
  // bleibt für Zähler-Zuordnung/-Anzeige unverändert der unnummerierte Name.
  const anzeigeNamen = nummeriereGleicheNamen(protokoll.raeume.map(effektiverRaumName));

  const touch = istTouch();

  return (
    <>
      <section aria-label="Räume auswählen" className="immo-glas flex flex-col" style={{ borderRadius: 26, padding: 18, gap: 12 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>Räume auswählen</span>
        {katalogFehler ? (
          <LadeHinweis text="Raumkatalog konnte nicht geladen werden — Verbindung prüfen." aufErneutVersuchen={ladeKatalogListe} />
        ) : (
          <div className="flex flex-wrap" style={{ gap: 8 }}>
            {katalog.map(k => {
              // „Sonstiger Raum" ist mehrfach anlegbar (Praxis-Ergänzung) — der Chip toggelt bewusst NICHT,
              // sondern legt bei jedem Klick einen weiteren Block an; die Anzahl steht daneben.
              const istSonstiger = k.raum === SONSTIGER_RAUM;
              const anzahl = protokoll.raeume.filter(r => r.raum === k.raum).length;
              const gewaehlt = !istSonstiger && anzahl > 0;
              const stil = raumStil(k.raum);
              const RI = RAUM_ICON[stil.symbol];
              return (
                <button key={k.id} type="button" onClick={() => (istSonstiger ? fuegeRaumHinzu(k.raum) : toggleRaum(k.raum))} aria-pressed={istSonstiger ? undefined : gewaehlt}
                  data-tip={istSonstiger ? 'Sonstiger Raum – Jeder Tipp legt einen weiteren Raum an, Bezeichnung frei wählbar' : gewaehlt ? `${k.raum} ist gewählt – Erneut tippen entfernt den Raum; sind schon Angaben erfasst, wird vorher nachgefragt` : `${k.raum} – Tippen nimmt den Raum in den Rundgang auf`}
                  className="inline-flex items-center"
                  style={{ height: touch ? 44 : 40, padding: '0 12px 0 10px', gap: 7, borderRadius: 22, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13.5, fontWeight: 800,
                    background: gewaehlt ? stil.farbe : 'var(--fill)', color: gewaehlt ? schriftAuf(stil.farbe) : 'var(--ink)',
                    boxShadow: gewaehlt ? `0 8px 18px -10px ${stil.farbe}` : 'inset 0 0 0 1px var(--line)' }}>
                  <RI size={16} weight="fill" color={gewaehlt ? undefined : stil.farbe} />
                  {k.raum}{istSonstiger && anzahl > 0 ? ` (${anzahl})` : ''}
                  {gewaehlt ? <Check size={13} weight="bold" /> : <Plus size={13} weight="bold" style={{ opacity: 0.6 }} />}
                </button>
              );
            })}
          </div>
        )}
      </section>

      {zaehlerFehler && (
        <LadeHinweis text="Zähler der Einheit konnten nicht geladen werden — Verbindung prüfen." aufErneutVersuchen={ladeZaehlerStamm} />
      )}

      {protokoll.raeume.length === 0 ? (
        <div className="immo-glas flex items-center justify-center" style={{ borderRadius: 26, padding: '40px 20px', fontSize: 14, fontWeight: 600, color: 'var(--ink3)' }}>
          Noch keine Räume gewählt.
        </div>
      ) : (
        <div className="grid items-start" style={{ gap: 12, gridTemplateColumns: 'repeat(auto-fill, minmax(min(380px, 100%), 1fr))' }}>
          {protokoll.raeume.map((raumBlock, index) => {
            const kennung = raumKennung(raumBlock);
            const raumName = effektiverRaumName(raumBlock);
            const anzeigeName = anzeigeNamen[index];
            const autoErkannt = zaehlerFuerRaum(zaehlerStamm, raumName);
            // `?? []`: ein Raumblock aus einem älteren, lokal begonnenen Entwurf könnte
            // `zaehler_ids` selbst vermissen lassen (s. lib/uebergabe/lokal.ts) — ungeschützt
            // würde bereits das Rendern dieser Seite abstürzen, nicht erst eine Eingabe.
            const raumEintraege = protokoll.zaehler.filter(z => (raumBlock.zaehler_ids ?? []).includes(z.id));
            const manuellStammIds = new Set(raumEintraege.filter(z => z.zaehler_id !== null).map(z => z.zaehler_id));
            const manuellHinzugefuegt = zaehlerStamm.filter(z => manuellStammIds.has(z.id) && !autoErkannt.some(a => a.id === z.id));
            const stammZeilen = [...autoErkannt, ...manuellHinzugefuegt];
            const adHocZeilen = raumEintraege.filter(z => z.zaehler_id === null);
            const globalOffene = offeneZaehler(zaehlerStamm, protokoll.zaehler);
            const weitereZaehler = globalOffene.filter(z => !stammZeilen.some(s => s.id === z.id));

            return (
              <RaumKarte
                key={kennung}
                raumBlock={raumBlock}
                anzeigeName={anzeigeName}
                fotos={entwurf.fotos}
                stammZeilen={stammZeilen}
                adHocZeilen={adHocZeilen}
                weitereZaehler={weitereZaehler}
                // Blockgenau (raumEintraege statt des gesamten protokoll.zaehler): sonst
                // zeigt ein zweiter, gleichnamiger Raumblock einen vom ERSTEN Block bereits
                // erfassten Zählerstand fälschlich als "hier schon erfasst" an (Review-Fund,
                // s. holeOderErzeugeEintrag/zaehlerZuRaumHinzufuegen oben).
                holeEintrag={stammId => raumEintraege.find(z => z.zaehler_id === stammId)}
                aenderePruefpunkt={(name, patch) => aenderePruefpunkt(kennung, name, patch)}
                fotosHinzufuegen={(name, dateien) => { void fotosHinzufuegen(kennung, name, dateien); }}
                raumFotosHinzufuegen={dateien => { void raumFotosHinzufuegen(kennung, dateien); }}
                raumBezeichnungAendern={wert => aendereRaumBezeichnung(kennung, wert)}
                standAendernStamm={(stamm, stand) => standAendernStamm(kennung, raumName, stamm, stand)}
                fotoHinzufuegenStamm={(stamm, datei) => { void fotoHinzufuegenStamm(kennung, raumName, stamm, datei); }}
                standAendernAdHoc={standAendernAdHoc}
                fotoHinzufuegenAdHoc={(id, datei) => { void fotoHinzufuegenAdHoc(id, datei); }}
                zaehlerZuRaumHinzufuegen={stamm => zaehlerZuRaumHinzufuegen(kennung, raumName, stamm)}
                neuerZaehlerHinzufuegen={(art, nummer) => neuerZaehlerHinzufuegen(kennung, raumName, art, nummer)}
                raumEntfernen={() => { void entferneRaumBlock(kennung); }}
              />
            );
          })}
        </div>
      )}
      {dialoge}
    </>
  );
};

export default SchrittRundgang;
